import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import analyticsService from '../services/analyticsService';
import LoadingSpinner from '../components/LoadingSpinner';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const AnalyticsCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow-lg);
  transition: all 0.3s ease;

  &:hover {    box-shadow: var(--shadow-xl);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CardIcon = styled.div`
  font-size: 1.5rem;
  opacity: 0.7;
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  color: var(--text-muted);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecentActivity = styled.div`
  margin-top: auto;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActivityItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-muted);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ActivityValue = styled.span`
  color: var(--accent-cyan);
  font-weight: 600;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const ErrorContainer = styled.div`
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.3);
  border-radius: var(--radius-lg);
  padding: 2rem;
  text-align: center;
  color: var(--error-color);
`;

const RetryButton = styled(motion.button)`
  background: var(--accent-cyan);
  color: var(--primary-bg);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {    box-shadow: var(--shadow-lg);
  }
`;

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getOverview();
      if (response.success) {
        setAnalytics(response.data.overview);
      } else {
        setError(response.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    loadAnalytics();
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Header>
          <Title>Dashboard</Title>
        </Header>
        <LoadingContainer>
          <LoadingSpinner size="large" text="Loading analytics..." />
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <Header>
          <Title>Dashboard</Title>
        </Header>
        <ErrorContainer>
          <h3>Failed to Load Analytics</h3>
          <p>{error}</p>
          <RetryButton
            onClick={handleRetry}
          >
            Retry
          </RetryButton>
        </ErrorContainer>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>Dashboard</Title>
      </Header>
      
      <AnalyticsGrid>
        {/* Projects */}
        <AnalyticsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <CardHeader>
            <CardTitle>Projects</CardTitle>
          </CardHeader>
          <MetricValue>{analytics?.totalAgents || 0}</MetricValue>
          <RecentActivity>
            <ActivityItem>
              <span>New this week</span>
              <ActivityValue>{analytics?.recentActivity?.agentsCreatedLast7Days || 0}</ActivityValue>
            </ActivityItem>
          </RecentActivity>
        </AnalyticsCard>

        {/* Campaigns */}
        <AnalyticsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <CardHeader>
            <CardTitle>Campaigns</CardTitle>
          </CardHeader>
          <MetricValue>{analytics?.totalWorkflows || 0}</MetricValue>
        </AnalyticsCard>

        {/* Leads */}
        <AnalyticsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <CardHeader>
            <CardTitle>Leads</CardTitle>
          </CardHeader>
          <MetricValue>{analytics?.totalLeadsExtracted || 0}</MetricValue>
          <RecentActivity>
            <ActivityItem>
              <span>New this week</span>
              <ActivityValue>{analytics?.recentActivity?.leadsExtractedLast7Days || 0}</ActivityValue>
            </ActivityItem>
          </RecentActivity>
        </AnalyticsCard>

        {/* Emails sent */}
        <AnalyticsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CardHeader>
            <CardTitle>Emails sent</CardTitle>
          </CardHeader>
          <MetricValue>{analytics?.totalEmailsSent || 0}</MetricValue>
          <RecentActivity>
            <ActivityItem>
              <span>Sent this week</span>
              <ActivityValue>{analytics?.recentActivity?.emailsSentLast7Days || 0}</ActivityValue>
            </ActivityItem>
          </RecentActivity>
        </AnalyticsCard>

        {/* LinkedIn drafts */}
        <AnalyticsCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <CardHeader>
            <CardTitle>LinkedIn drafts</CardTitle>
          </CardHeader>
          <MetricValue>{analytics?.totalLinkedInMessagesDrafted || 0}</MetricValue>
          <RecentActivity>
            <ActivityItem>
              <span>Drafted this week</span>
              <ActivityValue>{analytics?.recentActivity?.linkedInMessagesDraftedLast7Days || 0}</ActivityValue>
            </ActivityItem>
          </RecentActivity>
        </AnalyticsCard>
      </AnalyticsGrid>
    </DashboardContainer>
  );
};

export default Dashboard;
